import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { SignUp } from "@/components/challenge/SignUp";
import { DayPage } from "@/components/challenge/DayPage";
import { CompletionPage } from "@/components/challenge/CompletionPage";
import { DAYS } from "@/components/challenge/dayContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "glow_up_challenge_user";

// View state machine:
//  "signup" -> "day" (1..5) -> "complete"

export default function Challenge() {
  const [view, setView] = useState("signup");
  const [user, setUser] = useState(null);
  const [currentDay, setCurrentDay] = useState(1);
  const [bootstrapping, setBootstrapping] = useState(true);
  const [completed, setCompleted] = useState(false);

  const persist = useCallback((u, day, isComplete = false) => {
    const data = { email: u.email, first_name: u.first_name, current_day: day, completed: isComplete };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, []);

  // Bootstrap from localStorage
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      setBootstrapping(false);
      return;
    }
    try {
      const saved = JSON.parse(raw);
      if (!saved?.email) {
        setBootstrapping(false);
        return;
      }
      // Rehydrate from server to respect authoritative current_day
      axios
        .get(`${API}/challenge/user`, { params: { email: saved.email } })
        .then(({ data }) => {
          setUser(data);
          setCurrentDay(data.current_day);
          const isComplete =
            (data.tags || []).includes("Challenge-Complete") || saved.completed;
          if (isComplete) {
            setCompleted(true);
            setView("complete");
          } else {
            setView("day");
          }
        })
        .catch(() => {
          localStorage.removeItem(STORAGE_KEY);
        })
        .finally(() => setBootstrapping(false));
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      setBootstrapping(false);
    }
  }, []);

  const handleSignupComplete = (u) => {
    setUser(u);
    setCurrentDay(u.current_day || 1);
    const isComplete = (u.tags || []).includes("Challenge-Complete");
    if (isComplete) {
      setCompleted(true);
      setView("complete");
      persist(u, 5, true);
    } else {
      setView("day");
      persist(u, u.current_day || 1, false);
    }
  };

  const handleAdvance = () => {
    const next = Math.min(currentDay + 1, 5);
    setCurrentDay(next);
    if (user) persist(user, next, false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleComplete = () => {
    setCompleted(true);
    setView("complete");
    if (user) persist(user, 5, true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (bootstrapping) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-cream"
        data-testid="bootstrap-loading"
      >
        <div className="font-serif-editorial italic text-2xl text-gold animate-gold-pulse px-6 py-4">
          Loading your glow up…
        </div>
      </div>
    );
  }

  if (view === "signup") {
    return <SignUp onComplete={handleSignupComplete} />;
  }

  if (view === "complete") {
    return <CompletionPage firstName={user?.first_name} />;
  }

  const content = DAYS[currentDay - 1];
  return (
    <DayPage
      key={currentDay}
      day={currentDay}
      content={content}
      email={user?.email}
      onAdvance={handleAdvance}
      onComplete={handleComplete}
    />
  );
}
